import React from "react";
import { AiAvatar } from "@/components/AiAvatar";
import { VoiceButton } from "@/components/VoiceButton";
import { CornerDownLeft, Highlighter, Loader2, RotateCcw, X } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EXAMPLES = [
  "Quítale el fondo", "Pon el título en azul y más grande", "Corrige la ortografía",
  "Traduce todo al inglés", "Mejora y limpia la imagen",
];

/** Chat lateral del asistente de IA del editor. */
export const AiChatPanel = ({
  open, onClose, msgs, busy, chatEndRef, canUndo, onUndo,
  value, onChange, onSend, markerRegion, onClearMarker, jobId, query,
}) => {
  if (!open) return null;
  return (
    <div data-testid="ai-chat-drawer"
      className="fixed top-0 right-0 h-full w-[340px] z-40 flex flex-col border-l shadow-2xl"
      style={{ borderColor: "#1B1B21", backgroundColor: "#08080C" }}>
      <div className="h-14 px-4 flex items-center justify-between border-b" style={{ borderColor: "#1B1B21" }}>
        <div className="flex items-center gap-2">
          <AiAvatar size={40} busy={busy} />
          <div>
            <div className="text-sm font-bold text-slate-100">Asistente del editor</div>
            <div className="text-[10px] text-slate-500">Escribe y yo lo preparo</div>
          </div>
        </div>
        <button data-testid="ai-chat-close" onClick={onClose}
          className="text-slate-400 hover:text-slate-200"><X className="w-5 h-5" /></button>
      </div>

      <div data-testid="ai-chat-messages" className="flex-1 overflow-y-auto p-4 space-y-3">
        {msgs.length === 0 && (
          <div className="text-xs text-slate-500 leading-relaxed">
            <p className="mb-2">👋 Dime qué quieres y yo hago todo el trabajo. Por ejemplo:</p>
            <ul className="space-y-1">
              {EXAMPLES.map((ex) => (
                <li key={ex}>
                  <button data-testid="ai-chat-example" onClick={() => onSend(ex)}
                    className="text-red-400 hover:text-red-300 text-left">• {ex}</button>
                </li>
              ))}
            </ul>
          </div>
        )}
        {msgs.map((m) => (
          <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div data-testid={`chat-msg-${m.role}`}
              className={`max-w-[85%] text-sm rounded-2xl px-3 py-2 ${m.role === "user"
                ? "bg-[#C2620E] text-white rounded-br-sm"
                : "bg-[#121218] text-slate-200 rounded-bl-sm border border-[#1B1B21]"}`}>
              {m.text}
              {m.undoable && canUndo && (
                <button data-testid="chat-undo-btn" onClick={onUndo}
                  className="mt-1.5 flex items-center gap-1 text-[11px] text-amber-300 hover:text-amber-200">
                  <RotateCcw className="w-3 h-3" /> Deshacer este cambio
                </button>
              )}
            </div>
          </div>
        ))}
        {busy && (
          <div className="flex justify-start">
            <div className="bg-[#121218] border border-[#1B1B21] text-slate-400 rounded-2xl rounded-bl-sm px-3 py-2 text-sm flex items-center gap-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Preparando…
            </div>
          </div>
        )}
        <div ref={chatEndRef} />
      </div>

      {markerRegion && (
        <div className="px-4 py-2 text-[11px] text-amber-300 border-t flex items-center gap-1.5"
          style={{ borderColor: "#1B1B21" }}>
          <Highlighter className="w-3.5 h-3.5" /> Enfocaré la zona que marcaste.
          <button onClick={onClearMarker} className="ml-auto text-slate-500 hover:text-slate-300">quitar</button>
        </div>
      )}

      <div className="p-3 border-t" style={{ borderColor: "#1B1B21" }}>
        <div className="flex items-end gap-2">
          <textarea data-testid="ai-chat-input" value={value} rows={2}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey && !busy) { e.preventDefault(); onSend(); }
            }}
            placeholder="Escríbeme lo que quieres…"
            className="flex-1 bg-[#0B0B10] border border-[#2A2A32] focus:border-[#FF9440] outline-none text-slate-100 text-sm rounded-lg px-3 py-2 resize-none" />
          <VoiceButton testId="ai-chat-voice" className="w-10 h-10 shrink-0" title="Dímelo por voz"
            url={`${BACKEND_URL}/api/editor/${jobId}/voice${query}`}
            onText={(t) => onSend(t)} />
          <button data-testid="ai-chat-send" onClick={() => onSend()} disabled={busy || !value.trim()}
            className="w-10 h-10 rounded-lg bg-gradient-to-r from-[#8A3C06] to-[#FF9440] hover:from-[#B4560E] hover:to-[#FFA85C] text-white flex items-center justify-center disabled:opacity-50 shrink-0">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CornerDownLeft className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AiChatPanel;
